/* eslint-disable @typescript-eslint/no-explicit-any */
import { getErrorMessage } from '@juvare/common-js/utils';
import { showAlertDialog } from '@juvare/common-web-ui/DialogHelper';
import React from 'react';
/**
 * Form config for hiding components. Supports conditional logic based on sibling components.
 * Very similar to disable form configuration, so any changes here should likely be reflected in the disable form config.
 * */
export function getHideFormConfig(params) {
    const { t, siblingCheckboxComponents, siblingNumberComponents, siblingRadioComponents, siblingSelectComponents, siblingRadioConfigs, siblingSelectConfigs, siblingTextComponents, siblingYesNoComponents, selectComponentValues, handleUpdateAnyValuesBasedOnSiblingConfig: onUpdateAnyValuesBasedOnSiblingConfig, } = params;
    return [
        {
            type: 'form.panel',
            key: 'hiddenConfig',
            children: [
                {
                    type: 'form.content',
                    html: `<h3>${t('common.visibility')}</h3>`,
                },
                {
                    type: 'form.radio',
                    key: 'type',
                    values: [
                        { value: 'visible', label: t('buildHiddenTab.hideConfig.visible') },
                        { value: 'hide', label: t('buildHiddenTab.hideConfig.hide') },
                        { value: 'show', label: t('buildHiddenTab.hideConfig.show') },
                        { value: 'custom', label: t('buildHiddenTab.hideConfig.custom') },
                    ],
                    style: {
                        inline: true,
                    },
                },
                {
                    type: 'form.panel',
                    hidden: {
                        custom: (handler) => {
                            var _a;
                            const when = (_a = handler.up().down('type')) === null || _a === void 0 ? void 0 : _a.getData();
                            return when !== 'hide' && when !== 'show';
                        },
                    },
                    children: [
                        {
                            type: 'form.select',
                            key: 'component',
                            label: t('buildHiddenTab.whenComponent'),
                            values: selectComponentValues,
                            hidden: {
                                when: '^type',
                                dataEq: 'visible',
                            },
                        },
                        {
                            type: 'form.radio',
                            key: 'dataEqCheckbox',
                            label: t('buildHiddenTab.checkbox'),
                            values: [
                                { value: true, label: t('buildHiddenTab.checked') },
                                { value: false, label: t('buildHiddenTab.notChecked') },
                            ],
                            validate: {
                                required: true,
                            },
                            hidden: {
                                custom: (handler) => { var _a; return !siblingCheckboxComponents.includes((_a = handler.up().down('component')) === null || _a === void 0 ? void 0 : _a.getData()); },
                            },
                        },
                        {
                            type: 'form.number',
                            key: 'dataEqNumber',
                            label: t('buildHiddenTab.number'),
                            validate: {
                                required: true,
                            },
                            hidden: {
                                custom: (handler) => { var _a; return !siblingNumberComponents.includes((_a = handler.up().down('component')) === null || _a === void 0 ? void 0 : _a.getData()); },
                            },
                        },
                        {
                            type: 'form.yesNo',
                            key: 'dataEqYesNo',
                            label: t('buildHiddenTab.yesNo'),
                            validate: {
                                required: true,
                            },
                            hidden: {
                                custom: (handler) => { var _a; return !siblingYesNoComponents.includes((_a = handler.up().down('component')) === null || _a === void 0 ? void 0 : _a.getData()); },
                            },
                        },
                        {
                            type: 'form.text',
                            key: 'dataEqText',
                            label: t('buildHiddenTab.textEquals'),
                            multiline: true,
                            rows: 2,
                            validate: {
                                required: true,
                            },
                            hidden: {
                                custom: (handler) => { var _a; return !siblingTextComponents.includes((_a = handler.up().down('component')) === null || _a === void 0 ? void 0 : _a.getData()); },
                            },
                        },
                        {
                            type: 'form.checkboxList',
                            key: 'dataInCheckboxList',
                            label: t('buildHiddenTab.valueIsOneOf'),
                            allowInvalidValues: true,
                            validate: {
                                required: true,
                            },
                            hidden: {
                                custom: (handler) => { var _a; return !siblingRadioComponents.includes((_a = handler.up().down('component')) === null || _a === void 0 ? void 0 : _a.getData()); },
                            },
                            event: {
                                onUpdateAny: (handler) => {
                                    var _a;
                                    return onUpdateAnyValuesBasedOnSiblingConfig(handler, siblingRadioConfigs[(_a = handler.up().down('component')) === null || _a === void 0 ? void 0 : _a.getData()]);
                                },
                            },
                        },
                        {
                            type: 'form.multiSelect',
                            key: 'dataInMultiSelect',
                            label: t('buildHiddenTab.valueIsOneOf'),
                            allowInvalidValues: true,
                            validate: {
                                required: true,
                            },
                            hidden: {
                                custom: (handler) => { var _a; return !siblingSelectComponents.includes((_a = handler.up().down('component')) === null || _a === void 0 ? void 0 : _a.getData()); },
                            },
                            event: {
                                onUpdateAny: (handler) => {
                                    var _a;
                                    return onUpdateAnyValuesBasedOnSiblingConfig(handler, siblingSelectConfigs[(_a = handler.up().down('component')) === null || _a === void 0 ? void 0 : _a.getData()]);
                                },
                            },
                        },
                        {
                            type: 'form.panel',
                            hidden: {
                                custom: (handler) => { var _a; return ((_a = handler.up().down('component')) === null || _a === void 0 ? void 0 : _a.getData()) !== '$'; },
                            },
                            children: [
                                {
                                    type: 'form.text',
                                    key: 'when',
                                    label: t('buildHiddenTab.componentQuery'),
                                    validate: {
                                        required: true,
                                    },
                                },
                                {
                                    type: 'form.radio',
                                    key: 'whenDataEqType',
                                    label: t('buildHiddenTab.valueEquals'),
                                    values: [
                                        { value: 'text', label: t('buildHiddenTab.specificText') },
                                        { value: 'number', label: t('buildHiddenTab.specificNumber') },
                                        { value: 'true', label: t('common.true') },
                                        { value: 'false', label: t('common.false') },
                                        { value: 'eqJson', label: t('buildHiddenTab.jsonValue') },
                                        { value: 'inJson', label: t('buildHiddenTab.oneOfJsonValues') },
                                    ],
                                    style: {
                                        inline: true,
                                    },
                                },
                                {
                                    type: 'form.number',
                                    key: 'whenDataEqNumber',
                                    label: t('buildHiddenTab.numberLabel'),
                                    validate: {
                                        required: true,
                                    },
                                    hidden: {
                                        custom: (handler) => { var _a; return ((_a = handler.up().down('whenDataEqType')) === null || _a === void 0 ? void 0 : _a.getData()) !== 'number'; },
                                    },
                                },
                                {
                                    type: 'form.text',
                                    key: 'whenDataEqText',
                                    label: t('buildHiddenTab.textLabel'),
                                    multiline: true,
                                    rows: 2,
                                    validate: {
                                        required: true,
                                    },
                                    hidden: {
                                        custom: (handler) => { var _a; return ((_a = handler.up().down('whenDataEqType')) === null || _a === void 0 ? void 0 : _a.getData()) !== 'text'; },
                                    },
                                },
                                {
                                    type: 'form.text',
                                    key: 'whenDataEqJson',
                                    label: t('buildHiddenTab.jsonLabel'),
                                    multiline: true,
                                    rows: 2,
                                    validate: {
                                        required: true,
                                        custom: (value) => {
                                            if (value) {
                                                try {
                                                    JSON.parse(value);
                                                }
                                                catch (e) {
                                                    return e.toString();
                                                }
                                            }
                                            return undefined;
                                        },
                                    },
                                    hidden: {
                                        custom: (handler) => { var _a; return ((_a = handler.up().down('whenDataEqType')) === null || _a === void 0 ? void 0 : _a.getData()) !== 'eqJson'; },
                                    },
                                },
                                {
                                    type: 'form.text',
                                    key: 'whenDataInJson',
                                    label: t('buildHiddenTab.jsonArray'),
                                    multiline: true,
                                    rows: 6,
                                    validate: {
                                        required: true,
                                        custom: (value) => {
                                            if (value) {
                                                try {
                                                    if (Array.isArray(JSON.parse(value))) {
                                                        return undefined;
                                                    }
                                                    else {
                                                        return t('buildHiddenTab.jsonArrayExpected');
                                                    }
                                                }
                                                catch (e) {
                                                    return e.toString();
                                                }
                                            }
                                            return undefined;
                                        },
                                    },
                                    hidden: {
                                        custom: (handler) => { var _a; return ((_a = handler.up().down('whenDataEqType')) === null || _a === void 0 ? void 0 : _a.getData()) !== 'inJson'; },
                                    },
                                },
                            ],
                        },
                    ],
                },
                {
                    type: 'form.panel',
                    hidden: {
                        custom: (handler) => {
                            var _a;
                            const when = (_a = handler.up().down('type')) === null || _a === void 0 ? void 0 : _a.getData();
                            return when !== 'custom';
                        },
                    },
                    children: [
                        {
                            type: 'form.text',
                            key: 'custom',
                            label: t('buildHiddenTab.customJsFunction'),
                            emptyAsNull: true,
                            multiline: true,
                            rows: 6,
                            style: {
                                monospace: true,
                            },
                            validate: {
                                required: true,
                                custom: (value) => {
                                    if (value) {
                                        try {
                                            Function('"use strict"; return (function(handler) { ' + value + '; })')();
                                        }
                                        catch (error) {
                                            return getErrorMessage(error);
                                        }
                                    }
                                    return undefined;
                                },
                            },
                        },
                        {
                            type: 'form.button',
                            label: t('buildHiddenTab.showExample'),
                            event: {
                                onClick: () => {
                                    showAlertDialog({
                                        title: t('buildHiddenTab.customVisibilityFunctionExample'),
                                        content: React.createElement("pre", { style: { margin: 0 } }, exampleHiddenCustomJavaScript),
                                    });
                                },
                            },
                            style: {
                                align: 'right',
                            },
                        },
                    ],
                },
            ],
        },
        {
            type: 'form.checkbox',
            key: 'clearDataOnHide',
            label: t('buildHiddenTab.clearDataOnHide'),
            description: t('buildHiddenTab.clearDataOnHideDescription'),
            hidden: {
                when: '^hiddenConfig/type',
                dataEq: 'visible',
            },
        },
    ];
}
const exampleHiddenCustomJavaScript = `// This code changes visibility based on combined value of two other components
const value1 = handler.up().down('component1')?.getData();
const value2 = handler.up().down('component2')?.getData();
return value1 === 'a' && value2 === 'b';
`;
